import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { Environment, UpdateEnvironmentData } from '@/types/environment';
import { EnvironmentType, EnvironmentStatus } from '@/types/api';
import { ArrowLeft, Layers } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea, ErrorAlert, Switch } from '@nimbitlabs/ui';

const environmentTypes: { value: EnvironmentType; label: string }[] = [
  { value: EnvironmentType.Development, label: 'Development' },
  { value: EnvironmentType.Uat, label: 'UAT' },
  { value: EnvironmentType.Staging, label: 'Staging' },
  { value: EnvironmentType.Production, label: 'Production' },
];

const statusOptions: { value: EnvironmentStatus; label: string }[] = [
  { value: EnvironmentStatus.Active, label: 'Active' },
  { value: EnvironmentStatus.Inactive, label: 'Inactive' },
  { value: EnvironmentStatus.Maintenance, label: 'Maintenance' },
];

export const EditEnvironment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getEnvironmentById, updateEnvironment: updateEnvironmentFn } = useEnvironmentsStore();
  
  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadEnvironment = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const env = await getEnvironmentById(id);
        setEnvironment(env as Environment);
      } catch (e) {
        console.error('Failed to load environment:', e);
      } finally {
        setLoading(false);
      }
    };
    loadEnvironment();
  }, [id, getEnvironmentById]);
  
  const [error, setError] = useState<unknown>(null);
  
  const [formData, setFormData] = useState<UpdateEnvironmentData>({
    name: '',
    description: '',
    type: EnvironmentType.Development,
    status: EnvironmentStatus.Active,
    url: '',
  });

  useEffect(() => {
    if (environment) {
      setFormData({
        name: environment.name,
        description: environment.description,
        type: environment.type,
        status: environment.status,
        url: '',
      });
    }
  }, [environment]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!environment) return;

    try {
      await updateEnvironmentFn(environment.id, formData);
      navigate(`/environments/${id}`);
    } catch (err) {
      console.error('Failed to update environment:', err);
      setError(err);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/environments">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Environments
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-medium mb-2">Loading environment...</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!environment) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/environments">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Environments
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <h3 className="text-lg font-medium mb-2">Environment not found</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to={`/environments/${id}`}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Environment
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Edit Environment</CardTitle>
          <CardDescription>
            Update environment information.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ErrorAlert error={error} className="mb-4" />
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Environment name"
                required
                maxLength={200}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value: Environment['type']) => setFormData(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select environment type" />
                </SelectTrigger>
                <SelectContent>
                  {environmentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Environment description"
                rows={4}
                required
                maxLength={1000}
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="space-y-0.5">
                <Label htmlFor="status" className="text-base font-medium">
                  Status
                </Label>
                <p className="text-sm text-muted-foreground">
                  {formData.status === EnvironmentStatus.Active ? 'Environment is active and operational' : 'Environment is inactive'}
                </p>
              </div>
              <Switch
                id="status"
                checked={formData.status === EnvironmentStatus.Active}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ 
                    ...prev, 
                    status: checked ? EnvironmentStatus.Active : EnvironmentStatus.Inactive 
                  }))
                }
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(`/environments/${id}`)}>
                Cancel
              </Button>
              <Button type="submit">
                Update Environment
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
